import { Button, Select, TextField, toast } from "@3sc/common-component";
import { Box, Grid, Stack, styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import CustomPhoneInput from "../../components/CustomPhoneInput";
import { tenantId, userId } from "../../config/localStorage";
import { nameValidation, userEditInitialValues } from "../../constant/constant";
import {
  createPayloadUserEdit,
  createUserData,
  updatedCities,
  updatedCountries,
  updatedStates,
} from "../../helper/commonHelper";
import {
  loaderStateChange,
  resetLoaderState,
} from "../../store/Slices/loading";
import { editUserAPI, getUserAPI } from "../../utils/ApiHandler";
import { STATUS_CODE } from "../../utils/const";
import { COM_MSG, ERROR_MSG } from "../../utils/messages";
import "./styles.scss";
import { validateField } from "../../helper/validationHelper";

import React, { useCallback, useEffect, useMemo, useState } from "react";
// ===============================
// Styled Components
// ===============================
const SCAIGrid = styled(Grid)(({ theme }) => ({
  margin: "0!important",
  padding: "1.5rem!important",
  width: "100%",
  backgroundColor: theme.palette.primary.background,
  borderRadius: "0.7rem",
}));

const SCAIGridItem = styled(Grid)(({ theme }) => ({
  padding: "1rem!important",
  width: "100%",
}));

// ===============================
// Field Config (Single source of truth)
// ===============================
const PROFILE_FIELDS = {
  first_name: {
    component: "TextField",
    name: "first_name",
    labelKey: "first_name",
    required: true,
    validation: nameValidation,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  last_name: {
    component: "TextField",
    name: "last_name",
    labelKey: "last_name",
    required: true,
    validation: nameValidation,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  email: {
    component: "TextField",
    name: "email",
    labelKey: "email_id",
    disabled: true,
    required: true,
    validation: "required|email",
    grid: { size: { xs: 12, md: 2.4 } },
  },
  mobile: {
    component: "Phone",
    name: "mobile",
    labelKey: "contact_number",
    required: true,
    validation: ["required", "phone", { max: "13" }],
    grid: { size: { xs: 12, md: 2.4 } },
  },
  empty: {
    component: "empty",
  },
  organization: {
    component: "TextField",
    name: "organization",
    labelKey: "client",
    disabled: true,
    required: true,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  business_unit: {
    component: "TextField",
    name: "business_unit",
    labelKey: "business_unit",
    disabled: true,
    required: true,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  team: {
    component: "TextField",
    name: "team",
    labelKey: "team",
    disabled: true,
    required: true,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  roles: {
    component: "TextField",
    name: "roles",
    labelKey: "Roles",
    disabled: true,
    required: true,
    grid: { size: { xs: 12, md: 2.4 } },
  },
  textField: {
    component: "textField",
  },
  address_line1: {
    component: "TextField",
    name: "address_line1",
    labelKey: "address_1",
    validation: [
      "alpha_num_dash_space",
      { max: "64" },
    ],
    grid: { size: { xs: 12, md: 2.4 } },
  },
  address_line2: {
    component: "TextField",
    name: "address_line2",
    labelKey: "address_2",
    validation: [
      "alpha_num_dash_space",
      { max: "64" },
    ],
    grid: { size: { xs: 12, md: 2.4 } },
  },
  country: {
    component: "Country",
    name: "country",
    labelKey: "country",
    grid: { size: { xs: 12, md: 2.4 } },
  },
  state: {
    component: "State",
    name: "state",
    labelKey: "state",
    grid: { size: { xs: 12, md: 2.4 } },
  },
  city: {
    component: "City",
    name: "city",
    labelKey: "city",
    grid: { size: { xs: 12, md: 2.4 } },
  },
  pincode: {
    component: "TextField",
    name: "pincode",
    labelKey: "pincode",
    validation: [
      "numeric",
      { min: "4" },
      { max: "16" },
    ],
    grid: { size: { xs: 12, md: 2.4 } },
  },
};

// ===============================
// Component
// ===============================
function MyProfile() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState(userEditInitialValues);
  const [errors, setErrors] = useState({});

  // ===============================
  // Fetch User Data
  // ===============================
  const fetchUserData = useCallback(async () => {
    if (!tenantId || !userId) return;

    try {
      dispatch(loaderStateChange({ isLoading: true, isParent: true }));

      const { status, data } = await getUserAPI({
        orgId: tenantId,
        userId,
      });

      if (STATUS_CODE.OK !== status) throw new Error(ERROR_MSG.SWR);

      const newFormData = createUserData({ data: data?.data });
      setFormData(newFormData);
    } catch {
      toast.error(t(ERROR_MSG.SWR));
    } finally {
      dispatch(resetLoaderState());
    }
  }, [dispatch, t]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // ===============================
  // Handlers
  // ===============================
  const handleChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "mobile") return;
    const error = validateField(value, PROFILE_FIELDS[name]?.validation);;

    setErrors((prev) => {
      if (error === prev[name]) return prev;

      const updated = { ...prev };

      if (error) {
        updated[name] = error;
      } else {
        delete updated[name];
      }

      return updated;
    });
  }, []);


  const handleNativeChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      handleChange(name, value);
    },
    [handleChange]
  );


  // ===============================
  // Submit Logic
  // ===============================
  const handleSubmit = useCallback(async () => {
    try {
      dispatch(loaderStateChange({ isLoading: true, isParent: true }));

      const payload = createPayloadUserEdit(formData, userId);

      const { status, data } = await editUserAPI({
        payload,
        orgId: tenantId,
        userId,
      });

      if (STATUS_CODE.OK !== status) throw new Error(ERROR_MSG.SWR);

      toast.success(t(data?.RESPONSE_CODE));
    } catch (error) {
      const resErr = JSON.stringify(error?.response?.data);
      toast.error(t(resErr));
    } finally {
      dispatch(resetLoaderState());
    }
  }, [dispatch, formData, t]);

  // ===============================
  // Memoized Options
  // ===============================
  const stateOptions = useMemo(() => {
    return formData.country
      ? updatedStates(formData.country.value)
      : [];
  }, [formData.country]);

  const cityOptions = useMemo(() => {
    return formData.state
      ? updatedCities(
        formData.country?.value,
        formData.state?.value
      )
      : [];
  }, [formData.country, formData.state]);

  // ===============================
  // Renderer
  // ===============================
  const renderField = (field) => {
    const gridProps = field.grid;
    const commonProps = {
      name: field.name,
      label: t(field.labelKey),
      value: formData[field.name] || "",
      disabled: field.disabled,
    };

    switch (field.component) {
      case "TextField":
        return (
          <SCAIGridItem item {...gridProps} key={field.name}>
            <TextField
              {...commonProps}
              required={field.required}
              onChange={handleNativeChange}
            />
            <span className="custom-error">
              {errors[field.name] && t(errors[field.name])}
            </span>
          </SCAIGridItem>
        );

      case "Phone":
        return (
          <SCAIGridItem item {...gridProps} key={field.name}>
            <CustomPhoneInput
              label={t(field.labelKey)}
              value={formData.mobile}
              onChange={(val) => handleChange("mobile", val)}
              setErrors={setErrors}
            />
          </SCAIGridItem>
        );

      case "Country":
        return (
          <SCAIGridItem item {...gridProps} key={field.name}>
            <Box>
              <Select
                label={t("country")}
                options={updatedCountries}
                value={formData.country}
                showSearch
                onChange={(value) => {
                  handleChange("country", value);
                  handleChange("state", null);
                  handleChange("city", null);
                }}
              />
            </Box>
          </SCAIGridItem>
        );

      case "State":
        return (
          <SCAIGridItem item {...gridProps} key={field.name}>
            <Box>
              <Select
                label={t("state")}
                disabled={!formData.country}
                options={stateOptions}
                value={formData.state}
                showSearch
                onChange={(value) => {
                  handleChange("state", value);
                  handleChange("city", null);
                }}
              />
            </Box>
          </SCAIGridItem>
        );

      case "City":
        return (
          <SCAIGridItem item {...gridProps} key={field.name}>
            <Box>
              <Select
                label={t("city")}
                disabled={!formData.state}
                options={cityOptions}
                value={formData.city}
                showSearch
                onChange={(value) => handleChange("city", value)}
              />
            </Box>
          </SCAIGridItem>
        );

      default:
        return <SCAIGridItem item {...gridProps} />;
    }
  };

  // ===============================
  // UI
  // ===============================
  return (
    <>
      <Stack
        direction={"column"}
        sx={{
          padding: "1.5rem",
          margin: "1.5rem",
          border: (theme) =>
            `0.1rem solid ${theme.palette.primary.borderColor}`,
          borderRadius: "0.8rem",
          gap: "1rem",
          height: "-webkit-fill-available",
        }}
      >
        <Typography
          sx={{
            color: (theme) => theme.palette.primary.activeText,
            fontSize: "1.4rem",
          }}
        >
          {t("my_profile")}
        </Typography>

        <Stack direction={"column"} justifyContent={"space-between"}>
          <SCAIGrid container spacing={3}>
            {Object.values(PROFILE_FIELDS).map(renderField)}
          </SCAIGrid>

          <Stack direction={"row-reverse"} sx={{
            p: "1rem 0.5rem"
          }}>
            <Button
              label={t("update")}
              styles={{ variant: "filled" }}
              disabled={Object.keys(errors)?.length}
              onClick={handleSubmit}
            />
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}

export default MyProfile;
